import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { Controller, UseFormReturn, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Dropdown } from "@/atoms-react/dropdown/index.react";
import InputLabel from "@/atoms-react/input/Input.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { RadioCard } from "@/atoms-react/radio-card/index.react";
import { Base, Label } from "@/atoms-react/text/Text.react";
import { AgentNatureMandate, LegalEntityLinkDto, MandateService } from "@/features/company/types";
import { StaticNatureMandate } from "@/features/user/types";
import { usei18n } from "@/features/utils/hooks/use-i18n";

type AddCompanyRepresentativeFormPrps = {
  natureMandates?: StaticNatureMandate[];
  pendingNatureMandates?: boolean;
  representative?: LegalEntityLinkDto | null;
} & UseFormReturn<LegalEntityLinkDto, LegalEntityLinkDto, LegalEntityLinkDto>;

const RepresentativeConfiguationForm = ({
  control,
  natureMandates,
  watch,
  setValue,
  getValues,
  formState,
  pendingNatureMandates
}: AddCompanyRepresentativeFormPrps) => {
  const { t } = useTranslation(["pass"]);
  const labelInputDivClassName = "md:flex items-center ";
  const natureMandateContainer = "w-full flex gap-2 justify-center align-center";
  const labelClassName = "flex w-full  md:w-40 min-w-40 mb-2 md:mb-0";
  const inputClassName = "flex grow w-full";
  const agentNatureRelation = watch("mandatoryInformation.agentNatureRelation");
  const { getLanguage } = usei18n();

  const showOtherInput =
    watch("mandatoryInformation.agentNatureRelation") === AgentNatureMandate.OTHER;
  return (
    <div>
      <div className="flex flex-col justify-between">
        <div className='div className="flex mb-5 justify-between'>
          <Base className="mt-6 hidden text-slate-300 md:block">
            {t("olkypass.accountPage.basicInfo.tabs.informations")}
          </Base>
        </div>
        <div className={twMerge(labelInputDivClassName, "mt-6")}>
          <div className="flex flex-row">
            <Label className={labelClassName}>{t("business.label.mandate_nature")}</Label>
            <div className="flex flex-col justify-center">
              <ExclamationCircleIcon
                className="mr-2 h-5 w-5 rounded-full text-blue-500"
                data-tooltip={t("business.tooltip_info.mandate_nature")}
                data-position="bottom"
              />
            </div>
          </div>
          <div className={natureMandateContainer}>
            {pendingNatureMandates ? (
              <Loader className="w-full justify-center" />
            ) : (
              <Controller
                control={control}
                rules={{
                  required: true
                }}
                name="mandatoryInformation.agentNatureRelation"
                render={({ field: { onChange, value } }) => (
                  <div className="flex w-full flex-col">
                    <Dropdown
                      required={!!formState?.errors?.mandatoryInformation?.agentNatureRelation}
                      touched={!!formState?.errors?.mandatoryInformation?.agentNatureRelation}
                      dataCy="agent_nature_relation"
                      search={true}
                      value={natureMandates
                        ?.find(
                          (nm: StaticNatureMandate) => value === nm.default_name.toLocaleUpperCase()
                        )
                        ?.id.toString()}
                      className={twMerge(
                        "w-full",
                        formState?.errors?.mandatoryInformation?.agentNatureRelation
                          ? "border-red-500"
                          : ""
                      )}
                      items={(natureMandates ?? [])?.map((value) => ({
                        key: value?.id?.toString(),
                        value: value?.default_name,
                        render: ({ key }) => (
                          <div className={"flex w-full"} key={key}>
                            <Base className={"mr-2"}>
                              {getLanguage() === "fr" ? value.name_fr : value.name}
                            </Base>
                          </div>
                        )
                      }))}
                      onChange={({ value }) => {
                        if (
                          value?.toString().toUpperCase() ===
                            AgentNatureMandate.EMPLOYEE_OF_THE_COMPANY &&
                          getValues("mandatoryInformation.agentCompanyName")
                        ) {
                          setValue("mandatoryInformation.agentCompanyName", undefined);
                          setValue("mandatoryInformation.isAgentLe", false);
                        }
                        onChange(value?.toString());
                      }}
                    />
                  </div>
                )}
              />
            )}
            {showOtherInput && (
              <Controller
                control={control}
                name="mandatoryInformation.agentNatureRelationOther"
                render={({ field: { onChange, value } }) => (
                  <div className="flex w-full flex-col">
                    <InputLabel
                      required={true}
                      data-tooltip={t("business.input.agent_nature_relation_other_placeholder")}
                      placeholder={t("business.input.agent_nature_relation_other_placeholder")}
                      noLatinCheck={true}
                      value={value as string}
                      error={
                        formState?.errors?.mandatoryInformation?.agentNatureRelationOther
                          ? t("atoms:general.required")
                          : ""
                      }
                      onChange={onChange}
                    />
                  </div>
                )}
              />
            )}
          </div>
        </div>

        <div className={twMerge(labelInputDivClassName, "mt-6")}>
          <div className="flex flex-row">
            <Label className={labelClassName}>
              {t("business_links.add_link.input.moralOrNot")}
            </Label>
            <div className="flex flex-col justify-center">
              <ExclamationCircleIcon
                className="mr-2 h-5 w-5 rounded-full text-blue-500"
                data-tooltip={t("business.tooltip_info.moralOrNot")}
                data-position="bottom"
              />
            </div>
          </div>
          <div className={inputClassName + "grid grid-cols-1 gap-2 md:grid-cols-2"}>
            <div className="flex w-full flex-col">
              <Controller
                control={control}
                rules={{
                  validate: (x) => x !== undefined
                }}
                name="mandatoryInformation.isAgentLe"
                render={({ field: { onChange } }) => (
                  <div className="flex w-full flex-col">
                    <div className={inputClassName + "grid grid-cols-1 gap-2 md:grid-cols-2"}>
                      <RadioCard
                        dataCy="is_agent_le_yes"
                        active={watch("mandatoryInformation.isAgentLe") ?? false}
                        onClick={() => onChange(true)}
                        disabled={
                          agentNatureRelation === AgentNatureMandate.EMPLOYEE_OF_THE_COMPANY
                        }
                        className={twMerge(
                          "border-2 border-slate-50 dark:border-slate-700 dark:text-white",
                          formState?.errors?.mandatoryInformation?.isAgentLe && "border-red-500"
                        )}
                      >
                        {getLanguage() === "fr" ? "Oui" : "Yes"}
                      </RadioCard>
                      <RadioCard
                        dataCy="is_agent_le_no"
                        className={twMerge(
                          "border-2 border-slate-50 dark:border-slate-700 dark:text-white",
                          formState?.errors?.mandatoryInformation?.isAgentLe ? "border-red-500" : ""
                        )}
                        active={watch("mandatoryInformation.isAgentLe") === false}
                        onClick={() => {
                          if (getValues("mandatoryInformation.agentCompanyName")) {
                            setValue("mandatoryInformation.agentCompanyName", undefined);
                          }
                          onChange(false);
                        }}
                      >
                        {getLanguage() === "fr" ? "Non" : "No"}
                      </RadioCard>
                    </div>
                  </div>
                )}
              />{" "}
              {watch("mandatoryInformation.isAgentLe") === true && (
                <Controller
                  control={control}
                  rules={{
                    required: watch("mandatoryInformation.isAgentLe") === true
                  }}
                  name="mandatoryInformation.agentCompanyName"
                  render={({ field: { onChange } }) => (
                    <InputLabel
                      dataCy="agent_company_name"
                      error={
                        formState?.errors?.mandatoryInformation?.agentCompanyName &&
                        watch("mandatoryInformation.isAgentLe") === true
                          ? t("atoms:general.required")
                          : ""
                      }
                      className="mt-2"
                      value={watch("mandatoryInformation.agentCompanyName") ?? undefined}
                      onChange={onChange}
                      required={false}
                    />
                  )}
                />
              )}
            </div>{" "}
          </div>
        </div>
        <div className={twMerge(labelInputDivClassName, "mt-6")}>
          <div className="flex flex-row">
            <Label className={labelClassName}>{t("business.label.contact_person")}</Label>
            <div className="flex flex-col justify-center">
              <ExclamationCircleIcon
                className="mr-2 h-5 w-5 rounded-full text-blue-500"
                data-tooltip={t("business.tooltip_info.contact_person")}
                data-position="bottom"
              />
            </div>
          </div>
          <div className={inputClassName + "grid grid-cols-1 gap-2 md:grid-cols-2"}>
            <Controller
              control={control}
              rules={{
                validate: (x) => {
                  return x !== undefined;
                }
              }}
              name="mandatoryInformation.isCommunicationContact"
              render={({ field: { onChange, value } }) => (
                <div className="flex w-full flex-col">
                  <div className={inputClassName + "grid grid-cols-1 gap-2 md:grid-cols-2"}>
                    <RadioCard
                      dataCy="is_communication_contact_yes"
                      className={twMerge(
                        "border-2 border-slate-50 dark:border-slate-700 dark:text-white",
                        formState?.errors?.mandatoryInformation?.isCommunicationContact &&
                          "border-red-500"
                      )}
                      active={value == true}
                      onClick={() => onChange(true)}
                    >
                      {getLanguage() === "fr" ? "Oui" : "Yes"}
                    </RadioCard>
                    <RadioCard
                      dataCy="is_communication_contact_no"
                      className={twMerge(
                        "border-2 border-slate-50 dark:border-slate-700 dark:text-white",
                        formState?.errors?.mandatoryInformation?.isCommunicationContact &&
                          "border-red-500"
                      )}
                      active={value == false}
                      onClick={() => onChange(false)}
                    >
                      {getLanguage() === "fr" ? "Non" : "No"}
                    </RadioCard>
                  </div>
                </div>
              )}
            />
          </div>
        </div>
        <div className={twMerge(labelInputDivClassName, "mt-6")}>
          <div className="flex flex-row">
            <Label className={labelClassName}>{t("business.label.alias_management")}</Label>
            <div className="flex flex-col justify-center">
              <ExclamationCircleIcon
                className="mr-2 h-5 w-5 rounded-full text-blue-500"
                data-tooltip={t("business.tooltip_info.alias_management")}
                data-position="bottom"
              />
            </div>
          </div>
          <div className={inputClassName + "grid grid-cols-1 gap-2 md:grid-cols-2"}>
            <div className="flex w-full flex-col">
              <div className={inputClassName + "grid grid-cols-1 gap-2 md:grid-cols-2"}>
                <RadioCard
                  dataCy="is_alias_manager_yes"
                  className={twMerge(
                    "border-2 border-slate-50 dark:border-slate-700 dark:text-white"
                  )}
                  active={(watch("mandatoryInformation.mandateServices") ?? [])?.includes(
                    MandateService.ALIASES_MANAGEMENT
                  )}
                  onClick={() =>
                    setValue(
                      "mandatoryInformation.mandateServices",
                      [
                        ...(getValues("mandatoryInformation.mandateServices") ?? []),
                        MandateService.ALIASES_MANAGEMENT
                      ],
                      { shouldDirty: true }
                    )
                  }
                >
                  {getLanguage() === "fr" ? "Oui" : "Yes"}
                </RadioCard>
                <RadioCard
                  dataCy="is_alias_manager_no"
                  className={twMerge(
                    "border-2 border-slate-50 dark:border-slate-700 dark:text-white"
                  )}
                  active={
                    !watch("mandatoryInformation.mandateServices")?.includes(
                      MandateService.ALIASES_MANAGEMENT
                    )
                  }
                  onClick={() =>
                    setValue(
                      "mandatoryInformation.mandateServices",
                      getValues("mandatoryInformation.mandateServices")?.filter(
                        (data) => data !== MandateService.ALIASES_MANAGEMENT
                      ),
                      { shouldDirty: true }
                    )
                  }
                >
                  {getLanguage() === "fr" ? "Non" : "No"}
                </RadioCard>
              </div>
            </div>
          </div>
        </div>
        <div className={twMerge(labelInputDivClassName, "mt-6")}>
          <div className="flex flex-row">
            <Label className={labelClassName}>{t("business.label.admin_kypay")}</Label>
            <div className="flex flex-col justify-center">
              <ExclamationCircleIcon
                className="mr-2 h-5 w-5 rounded-full text-blue-500"
                data-tooltip={t("business.tooltip_info.admin_kypay")}
                data-position="bottom"
              />
            </div>
          </div>
          <div className={inputClassName + "grid grid-cols-1 gap-2 md:grid-cols-2"}>
            <div className="flex w-full flex-col">
              <div className={inputClassName + "grid grid-cols-1 gap-2 md:grid-cols-2"}>
                <RadioCard
                  dataCy="is_kypay_manager_yes"
                  className={twMerge(
                    "border-2 border-slate-50 dark:border-slate-700 dark:text-white"
                  )}
                  active={(watch("mandatoryInformation.mandateServices") ?? []).includes(
                    MandateService.KYPAY_WITHDRAW
                  )}
                  onClick={() =>
                    setValue(
                      "mandatoryInformation.mandateServices",
                      [
                        ...(getValues("mandatoryInformation.mandateServices") ?? []),
                        MandateService.KYPAY_WITHDRAW
                      ],
                      { shouldDirty: true }
                    )
                  }
                >
                  {getLanguage() === "fr" ? "Oui" : "Yes"}
                </RadioCard>
                <RadioCard
                  dataCy="is_kypay_manager_no"
                  className={twMerge(
                    "border-2 border-slate-50 dark:border-slate-700 dark:text-white"
                  )}
                  active={
                    !(watch("mandatoryInformation.mandateServices") ?? []).includes(
                      MandateService.KYPAY_WITHDRAW
                    )
                  }
                  onClick={() =>
                    setValue(
                      "mandatoryInformation.mandateServices",
                      (getValues("mandatoryInformation.mandateServices") ?? []).filter(
                        (data) => data !== MandateService.KYPAY_WITHDRAW
                      ),
                      { shouldDirty: true }
                    )
                  }
                >
                  {getLanguage() === "fr" ? "Non" : "No"}
                </RadioCard>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export const RepresentativeConfiguration = (props: {
  representative?: LegalEntityLinkDto | null;
  natureMandates?: StaticNatureMandate[];
  pendingNatureMandates?: boolean;
}) => {
  const methods = useFormContext<LegalEntityLinkDto>();

  return <RepresentativeConfiguationForm {...methods} {...props} />;
};
